import { useEffect, useRef, RefObject } from 'react';
import tagularEvent from '../utils/tagularEvent';
import { useIntersect } from './useIntersect';

/**
 * Fires tagular view events when the observed element intersects the viewport.
 * Mirrors se-supersonic-ui useViewEvent: uses useIntersect, then fires each event
 * when the ref element is intersecting and disconnects by setting nodes to [].
 *
 * Used by per-node visibility consumers (PiiFormSection, DCS, TextQuestion,
 * InfoBanner, etc.). Step-level entry-point lifecycle events are not routed
 * through this hook — they are emitted by `TrackingProvider` via
 * `useEntryPointEventFiring`.
 */
const useViewEvents = (
  events: Array<{ name: string; payload?: unknown }>,
): { ref: RefObject<HTMLElement | null> } => {
  const { setNodes, entries } = useIntersect();
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    if (!ref.current) return;
    setNodes([ref.current]);
    entries?.forEach((entry) => {
      if (entry?.isIntersecting) {
        events.forEach((event) => {
          const { name, payload } = event;
          const payloadObj =
            payload && typeof payload === 'object' && !Array.isArray(payload)
              ? (payload as Record<string, unknown>)
              : {};
          tagularEvent(name, payloadObj);
        });
        setNodes([]);
      }
    });
  }, [entries?.[0]?.isIntersecting]);

  return { ref };
};

export default useViewEvents;
